import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, qs } from '../api';
import { Button, Card, ErrorMessage, when } from '../ui';

export type DocumentRecordType = 'sale' | 'purchase' | 'stocktake' | 'shipment';

/**
 * Print a document. The server makes a PDF of this page, keeps it on the record and notes who printed it, then the
 * PDF opens in a new tab ready to print or save. The button hides itself in the PDF.
 */
export function PrintDocumentButton({ recordType, recordId, kind, label = 'Print / save PDF' }: { recordType: DocumentRecordType; recordId: number; kind: string; label?: string }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Window | null>(null);
  const print = useMutation({
    mutationFn: () => api<{ document: { id: number } }>('/api/v1/documents/print', { body: { recordType, recordId, kind } }),
    onSuccess: ({ document }) => {
      if (tab && !tab.closed) tab.location.href = `/api/v1/documents/${document.id}/file`;
      else window.open(`/api/v1/documents/${document.id}/file`, '_blank');
      void qc.invalidateQueries({ queryKey: ['documents', recordType, recordId] });
    },
    onError: () => tab?.close(),
  });
  return (
    <span className="print:hidden">
      <Button
        variant="secondary"
        disabled={print.isPending}
        onClick={() => {
          // Opened now, in the click, so the browser does not take it for a pop-up.
          setTab(window.open('', '_blank'));
          print.mutate();
        }}
      >
        {print.isPending ? 'Making the PDF…' : label}
      </Button>
      {print.error && (
        <span className="mt-2 block">
          <ErrorMessage error={print.error} />
        </span>
      )}
    </span>
  );
}

interface PrintedDoc {
  id: number;
  label: string;
  byteSize: number;
  printedAt: string;
  printedBy: string | null;
}

/** The PDF copies kept for a record: what was printed, when and by whom. */
export function RecordDocuments({ recordType, recordId }: { recordType: DocumentRecordType; recordId: number }) {
  const docs = useQuery({
    queryKey: ['documents', recordType, recordId],
    queryFn: () => api<{ documents: PrintedDoc[] }>(`/api/v1/documents${qs({ recordType, recordId })}`).then((r) => r.documents),
  });
  if (!docs.data || docs.data.length === 0) return null;
  return (
    <Card title="Printed documents">
      <ul className="divide-y divide-slate-100 text-sm">
        {docs.data.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span>
              <span className="font-medium">{d.label}</span> · printed {when(d.printedAt)}
              {d.printedBy && ` by ${d.printedBy}`} · {Math.max(1, Math.round(d.byteSize / 1024))} KB
            </span>
            <a className="text-sky-700 hover:underline" href={`/api/v1/documents/${d.id}/file`} target="_blank" rel="noreferrer">
              Open PDF
            </a>
          </li>
        ))}
      </ul>
    </Card>
  );
}
